import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import { ChevronRight, ChevronDown, ChevronUp } from "lucide-react";

/**
 * DS Table primitives.
 *
 * 사용 예:
 *  <DSTable>
 *    <DSTable.Head>
 *      <DSTable.Th checkbox />
 *      <DSTable.Th sortable sortDirection="asc" onSort={...}>제품 이름</DSTable.Th>
 *      ...
 *    </DSTable.Head>
 *    <DSTable.Body>
 *      <DSTable.Row>
 *        <DSTable.Td checkbox>...</DSTable.Td>
 *        <DSTable.Td>...</DSTable.Td>
 *      </DSTable.Row>
 *    </DSTable.Body>
 *  </DSTable>
 */
const DSTable = ({ children, className = "", style }) => (
  // .ds-table-frame 가 외곽 border + radius + overflow:hidden 담당.
  // 안쪽 <table>은 border-collapse: collapse 라서 인접 셀 border 자동 1px로 머지됨.
  <div className="ds-table-frame">
    <table className={`ds-table ${className}`} style={style}>
      {children}
    </table>
  </div>
);

const DSTableHead = ({ children }) => (
  <thead className="ds-table__head">
    <tr>{children}</tr>
  </thead>
);

const DSTableBody = ({ children }) => <tbody>{children}</tbody>;

/**
 * @param {boolean} [props.sku]          - SKU 행 (bg gray-05, secondary text)
 * @param {boolean} [props.subcategory]  - 카테고리 트리 sub-row (figma 344:6336/6337)
 *                                         bg = #F9FAFB, text = Text/Secondary
 */
const DSTableRow = ({ children, sku = false, subcategory = false, onClick, style }) => (
  <tr
    className={[
      "ds-table__row",
      sku && "ds-table__row--sku",
      subcategory && "ds-table__row--subcategory",
    ]
      .filter(Boolean)
      .join(" ")}
    onClick={onClick}
    style={style}
  >
    {children}
  </tr>
);

/**
 * @param {object} props
 * @param {boolean} [props.checkbox]
 * @param {boolean} [props.sortable]
 * @param {"asc"|"desc"|null} [props.sortDirection]
 * @param {() => void} [props.onSort]
 * @param {string|number} [props.width]
 */
const DSTableTh = ({
  children,
  checkbox = false,
  centered = false,
  sortable = false,
  sortDirection = null,
  onSort,
  width,
  className = "",
  style,
}) => {
  const sortIcon =
    sortDirection === "asc"
      ? faSortUp
      : sortDirection === "desc"
        ? faSortDown
        : faSort;
  const classes = [
    "ds-table__th",
    checkbox && "ds-table__th--checkbox",
    centered && "ds-table__th--centered",
    sortable && "ds-table__th--sortable",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <th
      className={classes}
      style={{ width, ...style }}
      onClick={sortable ? onSort : undefined}
    >
      {children}
      {sortable && (
        <FontAwesomeIcon icon={sortIcon} className="ds-table__sort-icon" />
      )}
    </th>
  );
};

/**
 * @param {object} props
 * @param {boolean} [props.checkbox]   - 48w checkbox 셀
 * @param {boolean} [props.centered]   - 숫자/짧은 값 (가격/생성일/품절여부) — py=16 center 정렬
 * @param {boolean} [props.category]   - 카테고리 path 셀 — px=12 py=16
 * @param {boolean} [props.skuName]    - SKU 행의 옵션 이름 셀 — px=12 py=8
 * @param {boolean} [props.skuMarker]  - SKU 행의 좌측 ㄴ marker 셀
 */
const DSTableTd = ({
  children,
  checkbox = false,
  centered = false,
  category = false,
  skuName = false,
  skuMarker = false,
  colSpan,
  className = "",
  style,
  onClick,
}) => {
  const classes = [
    "ds-table__td",
    checkbox && "ds-table__td--checkbox",
    centered && "ds-table__td--centered",
    category && "ds-table__td--category",
    skuName && "ds-table__td--sku-name",
    skuMarker && "ds-table__td--sku-marker",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <td className={classes} colSpan={colSpan} style={style} onClick={onClick}>
      {children}
    </td>
  );
};

/**
 * 제품 이름 셀 (이미지 56×56 + 텍스트).
 * expandable=true 면 좌측에 chevron 표시 (옵션 펼치기).
 *
 * 이미지는 image slot(React node)으로 받아 외부에서 자유롭게 구성 가능.
 * (S3Image 등 외부 컴포넌트 직접 끼울 수 있도록)
 *
 * @param {React.ReactNode} [props.image] - 이미지 element. 없으면 placeholder.
 * @param {string} [props.imageUrl] - 단순 img URL (image 미사용 시 fallback)
 */
const DSProductNameCell = ({
  image,
  imageUrl,
  name,
  expandable = false,
  expanded = false,
  onToggleExpand,
}) => (
  <div className="ds-table__product-cell">
    {expandable &&
      (expanded ? (
        <ChevronUp
          size={14}
          className="ds-table__expand-chevron"
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand && onToggleExpand();
          }}
        />
      ) : (
        <ChevronDown
          size={14}
          className="ds-table__expand-chevron"
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand && onToggleExpand();
          }}
        />
      ))}
    <div className="ds-table__product-image">
      {image ? image : imageUrl ? <img src={imageUrl} alt={name || ""} /> : null}
      <div className="ds-table__product-image__dimmed" />
    </div>
    <span className="ds-table__product-name" title={name}>
      {name}
    </span>
  </div>
);

/**
 * 카테고리 트리 행의 "카테고리 이름" 셀.
 *  - depth=0: padding-left 12 (gap 6 + chevron 14 or 텍스트만)
 *  - depth=1: padding-left 32 (figma 344:6336)
 *  - depth=2+: padding-left 32 + (depth-1)*20 — SUSPECT: figma는 depth=2까지만 정의
 *  - hasChildren=true 면 chevron 표시. expanded prop 으로 회전.
 *  - hasChildren=false 면 leaf — chevron 자리(14+gap 6=20)만큼 추가 indent
 *    (figma 344:6337: depth=2 leaf는 pl=52)
 */
const DSCategoryNameCell = ({
  depth = 0,
  hasChildren = false,
  expanded = false,
  onToggle,
  name,
}) => {
  // depth 별 indent baseline (figma 정확값)
  //  depth 0: 12   (parent root)
  //  depth 1: 32   (sub category — pl-32 from figma)
  //  depth 2+: 32 + (depth-1)*20  (depth 2 = 52 — figma leaf pl=52 일치)
  const basePad = depth === 0 ? 12 : 32 + (depth - 1) * 20;
  // leaf 인 경우 chevron 자리 만큼 pad 추가 (chevron 14 + gap 6 = 20)
  const paddingLeft = hasChildren ? basePad : basePad + 20;
  return (
    <div className="ds-table__category-name-cell" style={{ paddingLeft }}>
      {hasChildren && (
        // 우진 지시: ">"(ChevronRight) 대신 lucide ChevronUp/Down 사용
        //   펼침=▲(ChevronUp) / 접힘=▼(ChevronDown)
        expanded ? (
          <ChevronUp
            size={14}
            className="ds-table__category-name-cell__chevron"
            onClick={(e) => {
              e.stopPropagation();
              onToggle && onToggle();
            }}
            style={{ cursor: "pointer" }}
          />
        ) : (
          <ChevronDown
            size={14}
            className="ds-table__category-name-cell__chevron"
            onClick={(e) => {
              e.stopPropagation();
              onToggle && onToggle();
            }}
            style={{ cursor: "pointer" }}
          />
        )
      )}
      <span className="ds-table__category-name-cell__text" title={name}>
        {name}
      </span>
    </div>
  );
};

/**
 * 카테고리 path 셀 (figma 357:8968).
 *  - 한 카테고리: 자기 path (대 > 중 > 소) 를 chevron 으로 join.
 *  - 여러 카테고리: 첫 카테고리만 path 로 표시 + "+ 외 N" Text/Tertiary.
 *
 * @param {Array<Array<string>>} props.paths - 카테고리별 path 의 segments 배열.
 *   예: [["음료","커피","라떼"], ["신메뉴"]] → "음료 > 커피 > 라떼" + "+ 외 1"
 * @param {Array<string>} [props.segments] - (deprecated) 단일 path 호환용.
 */
const DSCategoryPathCell = ({ paths, segments }) => {
  // 호환: segments 가 들어오면 paths=[segments]
  const resolvedPaths = paths || (segments ? [segments] : []);
  const firstPath = resolvedPaths[0] || [];
  const extraCount = Math.max(0, resolvedPaths.length - 1);
  return (
    <div className="ds-table__category-path">
      <span className="ds-table__category-path__segments">
        {firstPath.map((seg, idx) => (
          <React.Fragment key={`${seg}-${idx}`}>
            {idx > 0 && (
              <ChevronRight
                size={14}
                className="ds-table__category-path__chevron"
              />
            )}
            <span className="ds-table__category-path__seg">{seg}</span>
          </React.Fragment>
        ))}
      </span>
      {extraCount > 0 && (
        <span className="ds-table__category-path__extra">+ 외 {extraCount}</span>
      )}
    </div>
  );
};

DSTable.Head = DSTableHead;
DSTable.Body = DSTableBody;
DSTable.Row = DSTableRow;
DSTable.Th = DSTableTh;
DSTable.Td = DSTableTd;
DSTable.ProductNameCell = DSProductNameCell;
DSTable.CategoryPathCell = DSCategoryPathCell;
DSTable.CategoryNameCell = DSCategoryNameCell;

export default DSTable;
